<script lang="ts" setup>
import { useClaims } from '@auth/useClaims';
import { environmentName, showBanner } from '@environment/environment';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import VBreadcrumb from '@shared/component/VBreadcrumb/VBreadcrumb.vue';
import VHeading from '@shared/typography/VHeading/VHeading.vue';
import VSidePanel from '@shared/component/VSidePanel/VSidePanel.vue';
import { navigateToUrlWithPostRequest } from '@tools/helpers';
import { computed, reactive, watch } from 'vue';
import { hasPermission } from '@permission/hasPermission';
import AppLayoutBerichten from '@menu-lib/components/AppLayout/AppLayoutBerichten.vue';
import AppLayoutImpersonationBar from '@menu-lib/components/AppLayout/AppLayoutImpersonationBar.vue';
import { useBreadcrumbStore } from '../../breadcrumb/breadcrumbStore';
import AppLayoutContact from './AppLayoutContact.vue';
import AppLayoutCrvp from './AppLayoutCrvp.vue';
import AppLayoutLogo from './AppLayoutLogo/AppLayoutLogo.vue';
import AppLayoutMenuAccount from './AppLayoutMenuAccount.vue';
import AppLayoutMenuContact from './AppLayoutMenuContact.vue';
import AppLayoutMenuIntern from './AppLayoutMenuIntern.vue';
import AppLayoutMenuMain from './AppLayoutMenuMain.vue';
import GebruikerZoeken from '../GebruikerZoeken/GebruikerZoeken.vue';
import OmniSearchBar from '../OmniSearch/OmniSearchBar.vue';
import { type MenuItem } from './types';

const props = defineProps<{
  customMenuItems?: Array<MenuItem>;
  customMenuLoadingState?: boolean;
  navigateInFrame?: boolean;
}>();

const state = reactive({
  mainMenuOpen: false,
  searchUserOpen: false,
});

const breadcrumbStore = useBreadcrumbStore();
const claims = useClaims();

// het particulier-accent op de rand wint van de omgevingsrand-kleur;
const headerBorderClasses = computed(() => {
  const particulier = !!claims.value?.isParticulier;
  return {
    'border-b-2': showBanner || particulier,
    'border-b-scope-accent': particulier,
    'border-b-orange': environmentName === 'local' && showBanner && !particulier,
    'border-b-red': environmentName === 'test' && showBanner && !particulier,
    'border-b-blue': environmentName === 'acceptatie' && showBanner && !particulier,
  };
});

const closeSearchUser = () => (state.searchUserOpen = false);
const magPortefeuilleInzien = hasPermission('Portefeuille.Inzien');

watch(
  () => claims.value?.isWebmodule,
  (isWebmodule) => {
    const { style } = document.documentElement;
    const variableName = '--header-height';

    if (isWebmodule) {
      style.setProperty(variableName, '0');
    } else {
      style.removeProperty(variableName);
    }
  },
  { immediate: true },
);

function logout() {
  navigateToUrlWithPostRequest(
    `/identity/authentication/logout?returnUrl=${encodeURIComponent('/identity/user/verlaten')}`,
  );
}
</script>

<template>
  <div
    class="grow text-primary-900"
    :class="{
      'grid grid-cols-[auto_minmax(0,1fr)] grid-rows-[var(--header-height,0px)_1fr] max-lg:before:absolute max-lg:before:h-16 max-lg:before:w-full max-lg:before:shadow-sm max-lg:before:content-[\'\']':
        !claims?.isWebmodule,
    }"
    :style="{
      '--menu-container-width': `16rem`,
    }"
  >
    <template v-if="!claims?.isWebmodule">
      <div
        :class="[
          'sticky top-0 z-header flex border-0 border-b border-solid border-grey-200 bg-white px-2 lg:border-r lg:px-4',
          headerBorderClasses,
          { 'max-w-64': claims?.isTemporaryUser },
        ]"
      >
        <button
          v-if="!claims?.isTemporaryUser"
          aria-label="Open/sluit hoofdmenu"
          class="cursor-pointer appearance-none border-none bg-transparent px-2 text-lg text-inherit outline-none"
          type="button"
          @click="state.mainMenuOpen = !state.mainMenuOpen"
        >
          <FontAwesomeIcon :icon="['fas', 'bars']" />
        </button>

        <AppLayoutLogo class="min-w-0 px-2" />
      </div>

      <header
        :class="[
          'sticky top-0 z-header flex justify-end border-0 border-b border-solid border-grey-200 bg-white pl-2 lg:pl-6',
          headerBorderClasses,
          {
            'bg-env-local': environmentName === 'local' && showBanner,
            'bg-env-test': environmentName === 'test' && showBanner,
            'bg-env-acceptatie': environmentName === 'acceptatie' && showBanner,
          },
        ]"
      >
        <VBreadcrumb
          v-if="!claims?.isTemporaryUser"
          class="grow"
          :items="breadcrumbStore.breadcrumbs"
        />

        <nav class="flex px-2 lg:divide-x lg:divide-y-0 lg:divide-solid lg:divide-grey-200">
          <template v-if="!claims?.isTemporaryUser">
            <AppLayoutContact :particulier="claims?.isParticulier" class="hidden px-6 lg:flex" />
            <AppLayoutCrvp v-if="magPortefeuilleInzien !== false" class="max-xl:hidden lg:px-6" />
            <OmniSearchBar />

            <AppLayoutBerichten />

            <AppLayoutMenuIntern
              v-if="claims?.isInternal"
              v-model:search-user-open="state.searchUserOpen"
            />

            <AppLayoutMenuAccount />
          </template>

          <template v-else>
            <AppLayoutMenuContact />

            <button
              type="button"
              class="flex items-center gap-1 px-3 text-sm text-inherit no-underline lg:px-6"
              @click.prevent="logout"
            >
              <FontAwesomeIcon :icon="['far', 'right-from-bracket']" />
              <span class="max-sm:hidden">Uitloggen</span>
            </button>
          </template>
        </nav>
      </header>
    </template>

    <div
      v-if="!claims?.isTemporaryUser"
      :class="[
        'max-3xl:absolute 3xl:transition-[clip-path] 3xl:duration-300',
        {
          '3xl:clip-path-[rect(0_0_100%_0)]': !state.mainMenuOpen,
          '3xl:clip-path-[rect(0_100%_100%_0)]': state.mainMenuOpen,
        },
      ]"
    >
      <AppLayoutMenuMain
        v-model:open="state.mainMenuOpen"
        v-model:search-user-open="state.searchUserOpen"
        :custom-items="props.customMenuItems"
        :custom-loading-state="props.customMenuLoadingState"
        :navigate-in-frame="props.navigateInFrame"
      />
    </div>

    <div
      :class="[
        'flex flex-col',
        {
          'max-3xl:col-span-2 3xl:transition-[margin] 3xl:duration-300': !claims?.isTemporaryUser,
          'col-span-2': claims?.isTemporaryUser,
          '3xl:-ml-(--menu-container-width)': !claims?.isTemporaryUser && !state.mainMenuOpen,
        },
      ]"
    >
      <AppLayoutImpersonationBar />
      <slot></slot>
    </div>

    <VSidePanel :open="state.searchUserOpen" @close="closeSearchUser()">
      <template #header>
        <VHeading class="text-white" :level="3">Zoek een gebruiker</VHeading>
      </template>

      <template #default>
        <GebruikerZoeken v-if="state.searchUserOpen" @close="closeSearchUser()" />
      </template>
    </VSidePanel>
  </div>
</template>
